import React, { useEffect } from "react";
import { connect } from "react-redux";
import Dropzone from "react-dropzone";
import { config } from "../../../../../../ts/config";
import { CheckboxDescription, Input } from "../../../../../App/Common/Components/FormComponents";
import { allowedTypes, allowedTypesFormats } from "../../../../../App/Common/Styles/DNDImage";
import { IImagePart, IImageSettings } from "../../../../../components/course-author/types";
import { CourseInstructionPartOperations } from "../CourseInstructionPart.types";
import { FlexFormContainer, SeparatorErrorMessage } from "../separator/SpacerPart.styles";
import {
  Checkbox,
  FormContainer,
  ImageCheckboxContainer,
  ImageDropDownMessage,
  ImageFieldSet,
  ImagePreview,
  ImageUrlBox,
  OutlinedBox,
  PartImageDropZone,
  Tab,
  TabsContainer,
} from "./ImagePart.styles";
import { IState } from "../../../../../App/reducer";
import { AxiosInterceptedResult } from "../../../../../api/interceptors";
import { IFileUploadResponseData } from "../../../../../api/CourseAuthorApi";
import { isNil } from "ramda";

interface IImagePartProps {
  imagePart: IImagePart;
  edit: boolean;
  partOperations: CourseInstructionPartOperations;
  uploadFile: (file: File) => Promise<AxiosInterceptedResult<IFileUploadResponseData[]>>;
  uploadFileViaUrl: (link: string) => Promise<AxiosInterceptedResult<IFileUploadResponseData>>;
}

export const isInvalidImg = (files: File[] | undefined): boolean =>
  isNil(files) || files.length !== 1 || !allowedTypes.includes(files[0].type);

export const mkImageUrl = (imgId: string): string => `${config.saltcomplianceApiRoot}/images/${imgId}/_download`;

const ImagePart: React.FC<IImagePartProps> = props => {
  const { imagePart, partOperations, edit, uploadFile, uploadFileViaUrl } = props;
  const [activeTab, setActiveTab] = React.useState<string>("File Upload");
  const [backgroundImage, setBackgroundImage] = React.useState<string>();
  const [settings, setSettings] = React.useState<IImageSettings["settings"]>(imagePart.content.settings);
  const [errorMessage, setErrorMessage] = React.useState<string>("");
  const tabRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSettings(imagePart.content.settings);
  }, [imagePart.content.settings.fileImageId]);

  useEffect(() => {
    settings?.fileImageId && setBackgroundImage(mkImageUrl(settings.fileImageId));
  }, [settings?.fileImageId]);

  const updateSettings = (newSettings: IImageSettings["settings"]) => {
    setSettings(newSettings);
    partOperations.editImage({ settings: newSettings });
  };

  const handleImageUpload = (files: File[] | undefined) => {
    if (isInvalidImg(files)) {
      return alert(`Make sure to upload only one image\nFile type must be one of these: ${allowedTypesFormats}`);
    } else {
      uploadFile(files![0]).then(([error, response]) => {
        if (error) {
          return setErrorMessage("No response");
        }
        updateSettings({
          ...settings,
          fileImageId: response!.data[0].mediaId.slice(4),
        });
      });
    }
  };

  const onPropertiesChange = (e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>) => {
    if (["altText", "link"].includes(e.target.name)) {
      updateSettings({
        ...settings,
        [e.target.name.replace("imageSettings.", "")]: e.target.value,
      });
    } else if (["isOpenInNewWindow"].includes(e.target.name)) {
      updateSettings({
        ...settings,
        [e.target.name.replace("imageSettings.", "")]: (e as React.ChangeEvent<HTMLInputElement>).target.checked,
      });
    } else if (["align"].includes(e.target.name)) {
      updateSettings({
        ...settings,
        [e.target.name.replace("imageSettings.", "")]: e.target.value,
      });
    } else {
      uploadFileViaUrl(e.target.value).then(([error, response]) => {
        if (error) {
          setErrorMessage("No response");
        } else if (response) {
          updateSettings({
            ...settings,
            fileImageId: response.data.mediaId.slice(4),
          });
        }
      });
    }
  };

  const handleTabClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    const name = e.currentTarget.innerText;
    setActiveTab(name);
  };

  return (
    <>
      {edit ? (
        <>
          <SeparatorErrorMessage>{errorMessage}</SeparatorErrorMessage>
          <FlexFormContainer style={{ alignItems: "center" }}>
            <FormContainer>
              <TabsContainer>
                <Tab ref={tabRef} onClick={handleTabClick} active={activeTab === "File Upload"}>
                  File Upload
                </Tab>
                <Tab ref={tabRef} onClick={handleTabClick} active={activeTab === "URL"}>
                  URL
                </Tab>
              </TabsContainer>
              {activeTab === "File Upload" ? (
                <>
                  <Dropzone onDrop={handleImageUpload}>
                    {({ getRootProps, getInputProps }) => (
                      <div {...getRootProps()} style={{ width: "100%" }}>
                        <PartImageDropZone>
                          <OutlinedBox backgroundImage={backgroundImage}>
                            <ImageDropDownMessage backgroundImage={backgroundImage}>
                              Drop the file here or click to browse and upload
                            </ImageDropDownMessage>
                          </OutlinedBox>
                        </PartImageDropZone>
                        <Input
                          {...getInputProps()}
                          data-testid="image-uploader"
                          type="file"
                          name="file"
                          style={{ display: "none" }}
                        />
                      </div>
                    )}
                  </Dropzone>
                </>
              ) : (
                <>
                  <ImageUrlBox>
                    <OutlinedBox
                      backgroundImage={settings.fileImageId ? mkImageUrl(settings.fileImageId) : undefined}
                    />
                  </ImageUrlBox>
                  <ImageFieldSet>
                    <label htmlFor="url">URL</label>
                    <Input name="url" placeholder="Place the image URL" onBlur={onPropertiesChange} />
                  </ImageFieldSet>
                </>
              )}
              <ImageFieldSet>
                <label htmlFor="link">Alt Text</label>
                <Input
                  name="altText"
                  placeholder="Image's alternative description"
                  onBlur={onPropertiesChange}
                  defaultValue={String(settings?.altText)}
                />
              </ImageFieldSet>
              <ImageFieldSet>
                <label htmlFor="link">Link</label>
                <Input
                  name="link"
                  placeholder="URL when the image is clicked"
                  onBlur={onPropertiesChange}
                  defaultValue={String(settings?.link)}
                />
              </ImageFieldSet>
              <ImageCheckboxContainer>
                <Checkbox
                  type="checkbox"
                  name="isOpenInNewWindow"
                  onChange={onPropertiesChange}
                  defaultChecked={settings?.isOpenInNewWindow}
                />
                <CheckboxDescription>Open link in new window</CheckboxDescription>
              </ImageCheckboxContainer>
            </FormContainer>
          </FlexFormContainer>
        </>
      ) : (
        <>
          {settings.fileImageId && (
            <ImagePreview data-testid="img-part">
              <img src={mkImageUrl(settings.fileImageId)} alt={settings.altText} />
            </ImagePreview>
          )}
        </>
      )}
    </>
  );
};

const mapStateToProps = (state: IState) => ({
  uploadFile: state.App.CourseAuthor._.fileUploadApi.uploadFile,
  uploadFileViaUrl: state.App.CourseAuthor._.fileUploadApi.uploadFileViaUrl,
});

export default connect(mapStateToProps)(ImagePart);
